/*
  One modal, replacing sixteen hand-written copies.

  WHAT THE COPIES COST. Every modal in this client is the same five lines pasted again — a
  `.modal-backdrop` div with `onClick={onClose}`, an inner `.modal` that stops propagation, a header
  with an `<h2>` and a Close button. Measured across the app before this existed: **zero
  `role="dialog"`, zero `aria-modal`, no focus trap, no focus restore, and no modal anywhere that
  closed on Escape.** A keyboard-only user opening Analytics landed on the chart behind it, tabbed
  through a twenty-three-button toolbar, and could not dismiss it.

  None of that is because sixteen authors were careless. It is because the sixteenth copy inherits
  whatever the first one did, and the first one was written before any of it mattered.

  A DROP-IN, DELIBERATELY. This renders the same class names in the same nesting as the markup it
  replaces, and puts `data-testid` in the same place. Adopting it in a panel is therefore invisible
  on screen and invisible to the 4,844-line end-to-end suite — the panel simply gains a focus trap,
  Escape, and the aria wiring. Restyling happens later, once every modal is behind this one door.

  RADIX RATHER THAN HAND-ROLLED. A focus trap that actually works has to handle portalled content,
  shadow roots, `inert` support that varies by browser, iframes, and the sentinel nodes that catch a
  tab off the end. `@radix-ui/react-dialog` does that, is MIT, and passed the licence gate with its
  whole dependency tree in P9a. Writing a worse one by hand to save 44 packages would be a poor trade
  on a product whose users are on a keyboard all day.
*/
import * as Dialog from '@radix-ui/react-dialog';
import type { ReactNode } from 'react';

export function Modal({
  testId,
  title,
  onClose,
  children,
  footer,
  wide,
  describedBy,
}: {
  /** Goes on the panel element, exactly where the hand-written copies put it. */
  testId: string;
  title: string;
  onClose(): void;
  children: ReactNode;
  /** The action row. Omitted entirely rather than rendered empty, as the copies do. */
  footer?: ReactNode;
  /** The `.modal.wide` variant, for the register-shaped surfaces. */
  wide?: boolean;
  /** Id of an element describing the dialog, when one reads better than the title alone. */
  describedBy?: string;
}): JSX.Element {
  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        // Radix reports every dismissal — Escape, the overlay, the close button — through one
        // callback. The panels all expose `onClose`, so this is the single adapter between them.
        if (!next) onClose();
      }}
    >
      {/*
        NO `Dialog.Portal`, deliberately.

        A portal exists to escape a clipping or transformed ancestor. There is none here: every modal
        is rendered as a top-level sibling inside `ProjectView`'s returned tree, `.modal-backdrop` is
        already `position: fixed`, and nothing between it and the root carries a `transform`. So
        portalling would move the markup somewhere it does not need to be — and it would move it out
        of the container `mount()` returns, which is what every existing panel test addresses through
        `byId(v.container, …)`. Adoption would then break a dozen suites for no rendered difference.

        Escape, the focus trap and the background `aria-hidden` are all `Dialog.Content` and
        `DismissableLayer` behaviour; none of them needs the portal.
      */}
      {/*
        THE CONTENT IS NESTED INSIDE THE OVERLAY, which Radix's own examples treat as optional and
        this stylesheet does not.

        `.modal-backdrop` is not a scrim — it is `position: fixed; inset: 0; display: grid;
        place-items: center` with an explicit row track, and it is what CENTRES the panel and caps
        its height. Rendering Overlay and Content as siblings, which is the shape most Radix examples
        use, leaves `.modal` with no positioning of its own and puts a full-screen div on top of it.
        The end-to-end suite found it immediately: *"<div class='modal-backdrop'> intercepts pointer
        events"* on the first click inside a dialog.

        Nesting restores the exact parent→child relationship the CSS was written for, which is what
        the drop-in claim actually requires. Dismissal is unaffected: `DismissableLayer` decides
        "outside" from the Content node, so a press on the overlay area around it still counts.

        The overlay keeps its class but NOT the `onClick={onClose}` the copies all put on it — Radix
        already dismisses on an outside press, and wiring both would call `onClose` twice on one
        click, which several callers are not idempotent about. The inner `stopPropagation` is gone
        for the same reason: there is no longer a handler to stop.
      */}
      <Dialog.Overlay className="modal-backdrop">
        <Dialog.Content
          className={wide ? 'modal wide' : 'modal'}
          data-testid={testId}
          aria-describedby={describedBy}
        >
          <header>
            {/*
              A real `Dialog.Title`, which is what supplies `aria-labelledby`. Radix warns at runtime
              when it is missing, and the warning is right: a dialog with no accessible name is
              announced as "dialog" and nothing else.
            */}
            <Dialog.Title asChild>
              <h2>{title}</h2>
            </Dialog.Title>
            <Dialog.Close asChild>
              <button>Close</button>
            </Dialog.Close>
          </header>
          <div className="modal-body">{children}</div>
          {footer !== undefined && <footer>{footer}</footer>}
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog.Root>
  );
}
